import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { TopNav } from '@/components/TopNav';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Calendar, CaretDown, CaretRight, TrendUp,
} from '@phosphor-icons/react';
import {
  periodStartFor, addDays, formatISODate, formatPretty,
  isSameDay, currency, parseISO,
} from '@/lib/period';

const TODAY = new Date();
const OT_THRESHOLD = 80;

function dayLabel(d) {
  return d.toLocaleDateString('en-US', { weekday: 'short' }) + ', ' +
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function PeriodNav({ periodStart, onPrev, onNext, isCurrent, onJumpToday }) {
  return (
    <div className="flex items-center gap-2 flex-wrap mb-6">
      <Button
        data-testid="period-prev-btn"
        onClick={onPrev}
        variant="outline"
        className="rounded-full border-white/15 text-slate-200 hover:bg-white/5 hover:text-white px-5 h-10"
      >
        <ArrowLeft size={14} weight="bold" className="mr-2" /> Previous
      </Button>
      <Button
        data-testid="period-next-btn"
        onClick={onNext}
        variant="outline"
        className="rounded-full border-white/15 text-slate-200 hover:bg-white/5 hover:text-white px-5 h-10"
      >
        Next <ArrowRight size={14} weight="bold" className="ml-2" />
      </Button>
      {!isCurrent && (
        <Button
          data-testid="period-today-btn"
          onClick={onJumpToday}
          variant="ghost"
          className="rounded-full text-[#D4AF37] hover:bg-[#D4AF37]/10 px-4 h-10"
        >
          <Calendar size={14} weight="duotone" className="mr-1.5" /> Current
        </Button>
      )}
    </div>
  );
}

function ChequePreview({ totals, otRate }) {
  return (
    <Card
      data-testid="cheque-preview-card"
      className="!p-5 sm:!p-7 mb-6 sm:mb-8 rounded-2xl border"
      style={{
        background: 'linear-gradient(180deg, rgba(16,185,129,0.10) 0%, rgba(16,185,129,0.04) 100%)',
        borderColor: 'rgba(16,185,129,0.35)',
        boxShadow: '0 0 32px rgba(16,185,129,0.12)',
      }}
    >
      <div className="grid grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-4 sm:gap-y-5">
        <div>
          <div className="text-xs sm:text-sm text-emerald-200/80 mb-1">Total Hours</div>
          <div data-testid="cheque-total-hours" className="font-mono text-xl sm:text-3xl text-white">
            {totals.totalHours.toFixed(1)}
          </div>
        </div>
        <div>
          <div className="text-xs sm:text-sm text-emerald-200/80 mb-1">Total Tips</div>
          <div data-testid="cheque-total-tips" className="font-mono text-xl sm:text-3xl text-white">
            {currency(totals.tips)}
          </div>
        </div>
        <div>
          <div className="text-xs sm:text-sm text-emerald-200/80 mb-1">Overtime</div>
          <div data-testid="cheque-overtime-hours" className="font-mono text-xl sm:text-3xl text-white">
            {totals.otHours.toFixed(1)} <span className="text-sm sm:text-base text-emerald-200/60">hrs</span>
          </div>
        </div>
        <div>
          <div className="text-xs sm:text-sm text-emerald-200/80 mb-1">Est. Gross Pay</div>
          <div data-testid="cheque-gross" className="font-mono text-xl sm:text-3xl font-medium text-emerald-400">
            {currency(totals.gross)}
          </div>
        </div>
      </div>

      {totals.cashTips > 0 && (
        <div className="mt-4 sm:mt-5 pt-3 sm:pt-4 border-t border-emerald-200/10 flex items-center justify-between">
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-emerald-200/60">Cash tips (off-book)</span>
          <span data-testid="cheque-cash-tips" className="font-mono text-sm sm:text-base text-slate-300">{currency(totals.cashTips)}</span>
        </div>
      )}
    </Card>
  );
}

function DayRow({ date, entry, defaultRate, onSave, onDelete, isToday, showCash }) {
  const [hours, setHours] = useState(entry?.hours ?? '');
  const [tips, setTips] = useState(entry?.tips ?? '');
  const [cashTips, setCashTips] = useState(entry?.cash_tips ?? '');
  const [saving, setSaving] = useState(false);
  const debRef = useRef(null);
  const lastSavedRef = useRef(JSON.stringify(entry || null));
  const mountedRef = useRef(false);

  useEffect(() => {
    if (entry) {
      setHours((h) => (h === '' ? entry.hours : h));
      setTips((t) => (t === '' ? entry.tips : t));
      setCashTips((c) => (c === '' ? (entry.cash_tips ?? 0) : c));
    }
  }, [entry?.id]);

  const isEmpty = () =>
    !entry && (Number(hours) || 0) === 0 && (Number(tips) || 0) === 0 && (Number(cashTips) || 0) === 0;

  const triggerAutoSave = useCallback(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (isEmpty()) return;
    debRef.current = setTimeout(async () => {
      const rate = entry?.rate ?? defaultRate;
      const payload = {
        date: formatISODate(date),
        hours: Number(hours) || 0,
        rate: Number(rate),
        overtime: false,
        overtime_hours: 0,
        tips: Number(tips) || 0,
        cash_tips: Number(cashTips) || 0,
      };
      const sig = JSON.stringify(payload);
      if (sig === lastSavedRef.current) return;
      setSaving(true);
      try {
        await onSave(payload);
        lastSavedRef.current = sig;
      } catch (_) { /* parent toasts */ }
      finally { setSaving(false); }
    }, 700);
  }, [date, hours, tips, cashTips, defaultRate, onSave, entry]);

  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    triggerAutoSave();
    return () => debRef.current && clearTimeout(debRef.current);
  }, [hours, tips, cashTips, triggerAutoSave]);

  const iso = formatISODate(date);
  const showDot = isToday;

  return (
    <div
      data-testid={`day-row-${iso}`}
      className={`grid items-center gap-2 sm:gap-3 py-2.5 px-1 border-b border-white/5 last:border-b-0 ${
        showCash ? 'grid-cols-[1.15fr_1fr_1fr_1fr]' : 'grid-cols-[1.15fr_1fr_1fr]'
      }`}
    >
      <div className="flex items-center gap-1.5 text-slate-200 text-[13px] sm:text-[15px] leading-tight">
        {showDot && <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" aria-hidden />}
        <span className={`${isToday ? 'text-[#D4AF37] font-medium' : ''} truncate`}>{dayLabel(date)}</span>
        {saving && <span className="text-[9px] text-slate-500 font-mono ml-1">…</span>}
      </div>

      <Input
        data-testid={`day-hours-${iso}`}
        type="number"
        inputMode="decimal"
        step="0.25"
        value={hours}
        onChange={(e) => setHours(e.target.value)}
        placeholder="0"
        className="text-center bg-black/60 border-white/10 rounded-full h-11 font-mono text-white focus:border-[#D4AF37] text-base"
      />

      <Input
        data-testid={`day-tips-${iso}`}
        type="number"
        inputMode="decimal"
        step="0.01"
        value={tips}
        onChange={(e) => setTips(e.target.value)}
        placeholder="0"
        className="text-center bg-black/60 border-white/10 rounded-full h-11 font-mono text-white focus:border-emerald-500 text-base"
      />

      {showCash && (
        <Input
          data-testid={`day-cash-${iso}`}
          type="number"
          inputMode="decimal"
          step="0.01"
          value={cashTips}
          onChange={(e) => setCashTips(e.target.value)}
          placeholder="0"
          className="text-center bg-black/60 border-dashed border-white/10 rounded-full h-11 font-mono text-slate-300 focus:border-slate-300 focus:text-white text-base"
        />
      )}
    </div>
  );
}

function HistorySection({ refreshSignal, otRate }) {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/periods/summary');
        if (!cancelled) setPeriods(data.periods || []);
      } catch (_) {
        if (!cancelled) setPeriods([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshSignal]);

  if (loading) return <div className="text-sm text-slate-400">Loading history…</div>;
  if (periods.length === 0) {
    return (
      <div className="text-sm text-slate-500 border border-dashed border-white/10 rounded-md p-6 text-center">
        No prior pay periods yet. Your history will appear here.
      </div>
    );
  }

  return (
    <Accordion type="multiple" data-testid="history-accordion" className="space-y-2">
      {periods.map((p) => (
        <AccordionItem key={p.period_start} value={p.period_start} className="border border-white/10 rounded-md bg-[#121212] px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center justify-between w-full mr-4">
              <div className="text-left">
                <div className="font-display text-base">
                  {formatPretty(parseISO(p.period_start))} → {formatPretty(parseISO(p.period_end))}
                </div>
                <div className="text-xs text-slate-500">
                  {p.total_hours} hrs · {p.shifts} shift{p.shifts !== 1 ? 's' : ''}
                </div>
              </div>
              <span className="font-mono text-emerald-400">{currency(p.gross)}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 py-2">
              <Stat label="Regular pay" value={currency(p.regular_pay)} sub={`${p.regular_hours} hrs @ ${currency(p.rate)}`} />
              <Stat label="Overtime" value={currency(p.overtime_pay)} sub={`${p.overtime_hours} hrs @ ${currency(otRate)}`} accent="#F97316" />
              <Stat label="Tips" value={currency(p.tips)} accent="#10B981" />
              <Stat label="Gross" value={currency(p.gross)} accent="#D4AF37" />
            </div>
            {(p.cash_tips || 0) > 0 && (
              <div className="mt-2 text-xs text-slate-500 text-right">
                Cash tips (off-book): <span className="font-mono text-slate-300">{currency(p.cash_tips)}</span>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function Stat({ label, value, accent, sub }) {
  return (
    <div className="bg-black/40 border border-white/5 rounded p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="font-mono text-base mt-1" style={{ color: accent || '#F8FAFC' }}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function WeeklySnapshot({ pace, isCurrent }) {
  // Only useful for the active period, mid-way through (not at day 1, not after all days elapsed)
  if (!isCurrent) return null;
  if (pace.daysElapsed < 2 || pace.daysElapsed >= 14) return null;
  if (pace.shiftsSoFar === 0) return null;

  return (
    <Card
      data-testid="weekly-snapshot-card"
      className="!p-5 sm:!p-6 mb-8 rounded-2xl border"
      style={{
        background: 'linear-gradient(180deg, rgba(212,175,55,0.08) 0%, rgba(212,175,55,0.02) 100%)',
        borderColor: 'rgba(212,175,55,0.30)',
      }}
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendUp size={18} weight="duotone" className="text-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Weekly Snapshot</span>
        </div>
        <span className="text-xs text-slate-400 font-mono">
          Day {pace.daysElapsed} of 14 · {pace.shiftsSoFar} shift{pace.shiftsSoFar !== 1 ? 's' : ''} logged
        </span>
      </div>

      <p className="text-sm text-slate-300 mb-4">
        At your current pace, this pay period is on track for:
      </p>

      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Projected hours</div>
          <div data-testid="snapshot-hours" className="font-mono text-xl sm:text-2xl text-white">
            {pace.projectedHours.toFixed(1)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Projected tips</div>
          <div data-testid="snapshot-tips" className="font-mono text-xl sm:text-2xl text-white">
            {currency(pace.projectedTips)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Projected gross</div>
          <div data-testid="snapshot-gross" className="font-mono text-xl sm:text-2xl text-[#D4AF37]">
            {currency(pace.projectedGross)}
          </div>
        </div>
      </div>

      <p className="text-[11px] text-slate-500 mt-4">
        Based on {pace.shiftsSoFar} logged shift{pace.shiftsSoFar !== 1 ? 's' : ''} averaging {pace.avgHoursPerShift.toFixed(1)} hrs and {currency(pace.avgTipsPerShift)} in tips. Actual results will vary with the shifts you actually work.
      </p>
    </Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [periodStart, setPeriodStart] = useState(() => periodStartFor(TODAY));
  const [entries, setEntries] = useState([]);
  const [config, setConfig] = useState({ hr_options: [17, 14], overtime_rate: 25 });
  const [loading, setLoading] = useState(true);
  const [historyKey, setHistoryKey] = useState(0);
  const [defaultRate, setDefaultRate] = useState(17);
  const [showCash, setShowCash] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadEverything = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, entRes] = await Promise.all([
        api.get('/config'),
        api.get('/entries', { params: { period_start: formatISODate(periodStart) } }),
      ]);
      setConfig(cfgRes.data);
      setEntries(entRes.data.entries || []);
      // pick the most-used rate this period; fall back to first hr_option
      const rateCounts = {};
      (entRes.data.entries || []).forEach((e) => {
        const r = e.rate ?? cfgRes.data.hr_options[0];
        rateCounts[r] = (rateCounts[r] || 0) + 1;
      });
      const sorted = Object.entries(rateCounts).sort((a, b) => b[1] - a[1]);
      setDefaultRate(sorted.length ? Number(sorted[0][0]) : (cfgRes.data.hr_options[0] || 17));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [periodStart]);

  useEffect(() => { loadEverything(); }, [loadEverything]);

  const days = useMemo(() => Array.from({ length: 14 }, (_, i) => addDays(periodStart, i)), [periodStart]);
  const entriesByDate = useMemo(() => {
    const m = {};
    entries.forEach((e) => { m[e.date] = e; });
    return m;
  }, [entries]);

  // Auto-OT computation: OT kicks in over 80 hrs per bi-weekly period
  const totals = useMemo(() => {
    let totalHours = 0;
    let weighted = 0;
    let tips = 0;
    let cashTips = 0;
    entries.forEach((e) => {
      const h = e.hours || 0;
      totalHours += h;
      weighted += h * (e.rate || defaultRate);
      tips += e.tips || 0;
      cashTips += e.cash_tips || 0;
    });
    const avgRate = totalHours > 0 ? weighted / totalHours : defaultRate;
    const regHours = Math.min(totalHours, OT_THRESHOLD);
    const otHours = Math.max(0, totalHours - OT_THRESHOLD);
    const regPay = regHours * avgRate;
    const otPay = otHours * config.overtime_rate;
    return {
      totalHours,
      otHours,
      regHours,
      tips,
      cashTips,
      regPay,
      otPay,
      gross: regPay + otPay + tips,
      avgRate,
    };
  }, [entries, defaultRate, config.overtime_rate]);

  // Mid-period pace projection for the Weekly Snapshot card
  const pace = useMemo(() => {
    // days from period_start to today (inclusive), clamped to [1, 14]
    const msPerDay = 24 * 3600 * 1000;
    const raw = Math.floor((TODAY - periodStart) / msPerDay) + 1;
    const daysElapsed = Math.max(1, Math.min(14, raw));
    const shiftsWithHours = entries.filter((e) => (e.hours || 0) > 0);
    const shiftsSoFar = shiftsWithHours.length;
    const totalHours = shiftsWithHours.reduce((a, e) => a + (e.hours || 0), 0);
    const totalTips = shiftsWithHours.reduce((a, e) => a + (e.tips || 0), 0);
    const shiftsPerDay = shiftsSoFar / daysElapsed;
    const projectedShifts = Math.max(shiftsSoFar, shiftsPerDay * 14);
    const avgHoursPerShift = shiftsSoFar > 0 ? totalHours / shiftsSoFar : 0;
    const avgTipsPerShift = shiftsSoFar > 0 ? totalTips / shiftsSoFar : 0;
    const projectedHours = projectedShifts * avgHoursPerShift;
    const projectedTips = projectedShifts * avgTipsPerShift;
    const projRegHours = Math.min(projectedHours, OT_THRESHOLD);
    const projOtHours = Math.max(0, projectedHours - OT_THRESHOLD);
    const projRegPay = projRegHours * (totals.avgRate || defaultRate);
    const projOtPay = projOtHours * config.overtime_rate;
    const projectedGross = projRegPay + projOtPay + projectedTips;
    return {
      daysElapsed,
      shiftsSoFar,
      avgHoursPerShift,
      avgTipsPerShift,
      projectedHours,
      projectedTips,
      projectedGross,
    };
  }, [entries, periodStart, totals.avgRate, defaultRate, config.overtime_rate]);

  const saveEntry = async (payload) => {
    try {
      const { data } = await api.post('/entries', payload);
      setEntries((prev) => {
        const others = prev.filter((e) => e.date !== payload.date);
        return [...others, data.entry];
      });
      setHistoryKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  const deleteEntry = async (id) => {
    try {
      await api.delete(`/entries/${id}`);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      setHistoryKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const currentPeriodStart = periodStartFor(TODAY);
  const isCurrent = isSameDay(periodStart, currentPeriodStart);

  const rateForDisplay = totals.avgRate > 0 ? totals.avgRate : defaultRate;

  return (
    <div className="min-h-screen relative">
      <div className="grain-overlay" />
      <TopNav />

      <main
        className="max-w-4xl mx-auto px-4 sm:px-6 py-5 sm:py-8 relative z-10"
        style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }}
      >
        {/* Cheque Preview header */}
        <div className="mb-5 sm:mb-6">
          <h1 className="font-display text-2xl sm:text-4xl tracking-tighter mb-3 flex items-center gap-2">
            <span className="text-emerald-400 text-lg">▮</span>
            Cheque Preview
          </h1>
          <div className="space-y-1 text-sm sm:text-base">
            <p className="text-slate-300">
              <span className="text-slate-500">Pay period:</span>{' '}
              {dayLabel(periodStart)} <span className="text-slate-500">–</span> {dayLabel(addDays(periodStart, 13))}
            </p>
            <p className="text-slate-300">
              <span className="text-slate-500">Rate:</span>{' '}
              <span className="font-mono">${rateForDisplay % 1 === 0 ? rateForDisplay : rateForDisplay.toFixed(2)}/hr</span>
              <span className="text-slate-500"> · </span>
              <span className="text-slate-400">Overtime (80+ hrs): </span>
              <span className="font-mono">${config.overtime_rate}/hr</span>
            </p>
          </div>
        </div>

        <PeriodNav
          periodStart={periodStart}
          onPrev={() => setPeriodStart(addDays(periodStart, -14))}
          onNext={() => setPeriodStart(addDays(periodStart, 14))}
          isCurrent={isCurrent}
          onJumpToday={() => setPeriodStart(currentPeriodStart)}
        />

        <ChequePreview totals={totals} otRate={config.overtime_rate} />

        {user?.snapshot_enabled && (
          <WeeklySnapshot pace={pace} isCurrent={isCurrent} />
        )}

        {/* Entry table */}
        <Card className="card-surface !p-0 overflow-hidden mb-8">
          <div
            data-testid="entry-table-header"
            className={`grid items-center gap-2 sm:gap-3 px-3 sm:px-5 pt-4 pb-2 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-slate-500 ${
              showCash ? 'grid-cols-[1.15fr_1fr_1fr_1fr]' : 'grid-cols-[1.15fr_1fr_1fr]'
            }`}
          >
            <div>Date</div>
            <div className="text-center">Hours</div>
            <div className="text-center">Tips ($)</div>
            {showCash && <div className="text-center">Cash</div>}
          </div>
          <div className="px-3 sm:px-5 pb-2">
            {loading ? (
              <div className="text-slate-400 py-6">Loading…</div>
            ) : (
              days.map((d) => (
                <DayRow
                  key={formatISODate(d)}
                  date={d}
                  entry={entriesByDate[formatISODate(d)]}
                  defaultRate={defaultRate}
                  onSave={saveEntry}
                  onDelete={deleteEntry}
                  isToday={isSameDay(d, TODAY)}
                  showCash={showCash}
                />
              ))
            )}
          </div>
          <div className="px-4 sm:px-5 py-3 border-t border-white/5 flex items-center justify-between text-xs">
            <button
              data-testid="toggle-cash-tips"
              onClick={() => setShowCash((s) => !s)}
              className="text-slate-400 hover:text-[#D4AF37] transition-colors flex items-center gap-1.5"
            >
              {showCash ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
              {showCash ? 'Hide cash tips column' : 'Show cash tips column'}
            </button>
            <span className="text-slate-500">Auto-saves every entry</span>
          </div>
        </Card>

        {/* History */}
        <section>
          <button
            data-testid="history-toggle"
            onClick={() => setHistoryOpen((o) => !o)}
            className="flex items-center gap-2 text-slate-300 hover:text-[#D4AF37] transition-colors mb-3"
          >
            {historyOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
            <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500">History</span>
            <span className="font-display text-lg ml-1">Past pay periods</span>
          </button>
          {historyOpen && <HistorySection refreshSignal={historyKey} otRate={config.overtime_rate} />}
        </section>
      </main>
    </div>
  );
}
